import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import './_virtualized-select.scss';
import './_virtualized-select.dark-mode.scss';
import { List, CellMeasurer, CellMeasurerCache, AutoSizer } from 'react-virtualized';
import Select, { createFilter } from 'react-select';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { DEFAULT_CONTROL_STYLES, getOptions, getComponentsSelect, getScrollToIndex, checkIsGroupSelect, getMaxMenuHeight } from './helper';

const SelectVirtualized = ({ options, value, onChange, components, controlStyles, dropdownIndicatorStyles, valueContainerStyles, ...props }) => {
  const { darkMode } = useSelector((state) => state.general);

  const isGroup = useMemo(() => checkIsGroupSelect(options), [options]);
  const selectOptions = useMemo(() => getOptions(options, isGroup), [options, isGroup]);
  const selectComponents = useMemo(() => getComponentsSelect(components), [components]);
  const selectedValue = value;
  const scrollToIndex = useMemo(() => getScrollToIndex(selectOptions, value, isGroup), [selectOptions, value, isGroup]);
  const maxMenuHeight = getMaxMenuHeight(selectOptions, isGroup);

  const selectRef = useRef();

  const colors = {
    dark: {
      selectedColor: '#ffffff',
      placeholder: '#7b808e',
      bg: '#222327',
      active: '#2e3036',
    },
    white: {
      selectedColor: '#333333',
      placeholder: '#cccccc',
      bg: '#ffffff',
      active: '#f5f5f5',
    },
  };

  const formatOptionLabel = (data) => {
    const label = data?.short_name ? data.short_name : data.label;
    return <div>{label}</div>;
  };

  // eslint-disable-next-line react/prop-types
  const SingleValue = ({ selectProps, getStyles, data, className, ...props }) => (
    <div className={className} style={getStyles('singleValue', props)}>
      {/* eslint-disable-next-line react/prop-types */}
      {selectProps.getOptionLabel(data)}
    </div>
  );

  // eslint-disable-next-line react/prop-types
  const MenuList = ({ children, maxHeight }) => {
    const cache = new CellMeasurerCache({
      fixedWidth: true,
      minHeight: 20,
    });
    // eslint-disable-next-line react/prop-types
    const rowCount = _.isArray(children) ? children.length : 0;
    // eslint-disable-next-line react/prop-types
    if (_.isNull(children?.key)) {
      return <div>{children}</div>;
    }

    return (
      <AutoSizer disableHeight>
        {({ width }) => (
          <List
            deferredMeasurementCache={cache}
            height={maxHeight}
            overscanRowCount={0}
            rowCount={rowCount}
            rowHeight={cache.rowHeight}
            scrollToIndex={scrollToIndex}
            rowRenderer={({ index, key, parent, style }) => {
              // eslint-disable-next-line react/prop-types
              if (children[index].props.innerProps) {
                // eslint-disable-next-line react/prop-types
                delete children[index].props.innerProps.onMouseMove;
                // eslint-disable-next-line react/prop-types
                delete children[index].props.innerProps.onMouseOver;
              }
              return (
                <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
                  <div style={style} key={key}>
                    {children[index]}
                  </div>
                </CellMeasurer>
              );
            }}
            width={width}
          />
        )}
      </AutoSizer>
    );
  };

  return (
    <>
      <Select
        filterOption={createFilter({ ignoreAccents: false })}
        components={{ MenuList, SingleValue, ...selectComponents }}
        formatOptionLabel={formatOptionLabel}
        value={selectedValue}
        options={selectOptions}
        ref={selectRef}
        onChange={onChange}
        // onFocus={opOpen}
        styles={{
          input: (defaultStyles) => ({
            ...defaultStyles,
            color: darkMode ? colors.dark.selectedColor : colors.white.selectedColor,
          }),
          singleValue: (defaultStyles, { isDisabled }) => ({
            ...defaultStyles,
            color: darkMode ? (isDisabled ? '#464951' : colors.dark.selectedColor) : colors.white.selectedColor,
          }),
          placeholder: (defaultStyles) => ({
            ...defaultStyles,
            color: darkMode ? colors.dark.placeholder : colors.white.placeholder,
          }),
          valueContainer: (styles) => ({
            ...styles,
            marginTop: '-2px',
            ...valueContainerStyles,
          }),
          control: (styles, { isDisabled }) => ({
            ...styles,
            ...DEFAULT_CONTROL_STYLES(darkMode, isDisabled, colors),
            ...controlStyles,
          }),
          menu: (styles) => ({
            ...styles,
            zIndex: 1000,
            border: 'none',
            overflow: 'hidden',
            backgroundColor: darkMode ? colors.dark.bg : colors.white.bg,
          }),
          option: (styles, { isSelected }) => ({
            ...styles,
            paddingLeft: '10px',
            paddingRight: '10px',
            paddingTop: '3px',
            paddingBottom: '3px',
            cursor: 'pointer',
            color: darkMode ? '#dfe0e1' : '#333',
            backgroundColor: isSelected ? (darkMode ? colors.dark.active : colors.white.active) : darkMode ? '#222327' : 'transparent',
            '&:hover': {
              background: darkMode ? '#2e3036' : '#f5f5f5',
            },
            '&:before': isSelected
              ? {
                content: '"\\e013"',
                fontFamily: 'Glyphicons Halflings',
                fontStyle: 'normal',
                fontWeight: 400,
                position: 'absolute',
                right: '4px',
              }
              : null,
          }),
          dropdownIndicator: (styles) => ({
            ...styles,
            ...dropdownIndicatorStyles,
          }),
        }}
        maxMenuHeight={maxMenuHeight}
        classNamePrefix="select-virtualized"
        {...props}
      />
    </>
  );
};

SelectVirtualized.defaultProps = {
  controlStyles: {},
  dropdownIndicatorStyles: {},
  valueContainerStyles: {},
};

SelectVirtualized.propTypes = {
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  components: PropTypes.object,
  value: PropTypes.object,
  onChange: PropTypes.func,
  controlStyles: PropTypes.object,
  dropdownIndicatorStyles: PropTypes.object,
  valueContainerStyles: PropTypes.object,
};

export default SelectVirtualized;
